import { api } from "../api";
import { downloadBase64, triggerDownload, XLSX_MIME } from "../download";
import {
  buildPayload,
  sectionsFor,
  validateCurve,
  type Curve,
  type Evaluation,
  type Inputs,
  type SavedEvaluation,
  type Tool,
} from "./model";
type Reply<T> = { ok: boolean; data: T; message?: string };
export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await api<Reply<T>>(path, init);
  if (!response.ok)
    throw new Error(response.message || "요청을 완료하지 못했습니다.");
  return response.data;
}
export function calculate<T = Record<string, unknown>>(
  operation: string,
  payload: object,
): Promise<T> {
  return request(`/agent/rcps/calculate/${operation}`, {
    method: "POST",
    body: JSON.stringify({ payload }),
  });
}
export async function fetchRates(inputs: Inputs, kind: Tool): Promise<Inputs> {
  const { params: p } = inputs;
  if (!p.valuation_date)
    throw new Error("금리를 조회할 평가기준일을 입력해 주세요.");
  const fetchCurve = async (ratings: string): Promise<Curve> => {
    const d = await calculate<{
      columns: number[];
      values: number[];
      date?: string;
    }>(`rates-${p.rate_source}`, { date: p.valuation_date, ratings });
    if (
      !Array.isArray(d?.columns) ||
      !Array.isArray(d?.values) ||
      !d.values.some((v) => v !== 0)
    )
      throw new Error(
        `${ratings} 금리 데이터가 없습니다. 기준일을 확인하거나 직접 입력해 주세요.`,
      );
    const curve = {
      periods: d.columns,
      rates: d.values.map((v) => String(Number((v * 100).toFixed(6)))),
    };
    validateCurve(curve, ratings);
    return curve;
  };
  const [rf, kd] = await Promise.all([
    fetchCurve("국채"),
    kind === "so" ? Promise.resolve(inputs.kd) : fetchCurve(p.credit_rate),
  ]);
  return { ...inputs, rf, kd };
}
export async function runEvaluation(
  kind: Tool,
  inputs: Inputs,
): Promise<Evaluation> {
  // 프로젝트명은 선택 — 비었으면 칸의 기본 이름(DeepSearch 프로젝트·스톡옵션)으로 채워
  // 서버 요청·저장 이름·결과 제목에 같은 이름이 쓰이게 한다.
  const nameField = sectionsFor(kind).flatMap((s) => s.rows.flat()).find((f) => f.key === "valuation_company");
  if (nameField?.defaultHint && !(inputs.params.valuation_company || "").trim())
    inputs = { ...inputs, params: { ...inputs.params, valuation_company: nameField.defaultHint } };
  // 조회 전에 기본 입력부터 검증한다. 사용자가 0을 입력한 직접입력 금리는 유효하다.
  buildPayload(kind, inputs);
  const resolved =
    kind !== "volatility" && inputs.params.interest_input_method === "자동입력"
      ? await fetchRates(inputs, kind)
      : structuredClone(inputs);
  const payload = buildPayload(kind, resolved);
  if (kind !== "volatility") payload.interest_input_method = "직접입력";
  let result: unknown;
  if (kind === "bootstrapping") {
    const [rf, kd] = await Promise.all([
      calculate("bootstrapping", {
        ytms: payload.rf_ytms,
        ytm_periods: payload.rf_ytm_periods,
        basis: Number(resolved.params.rf_basis),
      }),
      calculate("bootstrapping", {
        ytms: payload.kd_ytms,
        ytm_periods: payload.kd_ytm_periods,
        basis: Number(resolved.params.kd_basis),
      }),
    ]);
    result = { rf, kd };
  } else result = await calculate(kind, payload);
  const numeric = (value: unknown) => typeof value === "number" && Number.isFinite(value);
  const record = result as Record<string, unknown> | null;
  const expected: Record<string, string[]> = {
    rcps: ["rcps_value", "conversion_value", "redemption_value", "bond_value"],
    bdt: ["BDT_value", "Bond_value", "Option_value"], so: ["stockoption_value"], volatility: ["volatility"],
  };
  const validCurve = (rows: unknown) => Array.isArray(rows) && rows.length > 0 && rows.every(row =>
    row && ["basis", "YTM", "spot_rate", "forward_rate"].every(key => numeric(row[key])));
  if (!record || (kind === "bootstrapping"
    ? !validCurve(record.rf) || !validCurve(record.kd)
    : expected[kind].some(key => !numeric(record[key]))))
    throw new Error("유효한 계산 결과를 받지 못했습니다. 입력값을 확인하고 다시 시도해 주세요.");
  return {
    kind,
    inputs: resolved,
    payload,
    result,
    calculated: new Date().toISOString(),
  };
}
export function listEvaluations<T = Evaluation>(): Promise<SavedEvaluation<T>[]> {
  return request("/agent/user-docs/rcps");
}
export function saveEvaluation(
  evaluation: Evaluation,
  id?: string,
): Promise<SavedEvaluation> {
  const docId = id || crypto.randomUUID().replaceAll("-", "");
  return request(`/agent/user-docs/rcps/${docId}`, {
    method: "PUT",
    body: JSON.stringify({
      name:
        evaluation.inputs.params.valuation_company ||
        `${evaluation.kind} · ${evaluation.inputs.params.valuation_date}`,
      summary: evaluation.kind,
      payload: evaluation,
    }),
  });
}
export async function exportEvaluation(
  e: Evaluation,
  format: "excel" | "pdf",
): Promise<void> {
  if (e.kind === "bootstrapping") {
    const file = await request<{ contentBase64: string; filename: string }>(
      "/agent/rcps/export/bootstrapping",
      {
        method: "POST",
        body: JSON.stringify({
          valuation_date: e.inputs.params.valuation_date,
          rf_basis: Number(e.inputs.params.rf_basis),
          kd_basis: Number(e.inputs.params.kd_basis),
          ...e.result as Record<string, unknown>,
        }),
      },
    );
    downloadBase64(file.contentBase64, file.filename, XLSX_MIME);
    return;
  }
  const payload: Record<string, unknown> = {
    ...e.payload,
    ...(!Array.isArray(e.result) && typeof e.result === "object"
      ? e.result
      : {}),
  };
  if (e.kind === "rcps")
    payload.callable_rcps_value = payload.calloption_value_issuer ?? 0;
  const d = await calculate<unknown>(`${e.kind}-${format}`, payload);
  const obj = d as Record<string, unknown> | null;
  const url =
    typeof d === "string"
      ? d
      : String(
          obj?.url || obj?.download_url || obj?.excel_url || obj?.pdf_url || "",
        );
  if (!/^https:\/\//i.test(url))
    throw new Error("다운로드 주소를 받지 못했습니다. 다시 시도해 주세요.");
  triggerDownload(url);
}
